import { apiClient } from './api.client';

export type IntercomPhase = 'idle' | 'connecting' | 'active' | 'stopping' | 'error';
export interface IntercomState { phase: IntercomPhase; error?: string; detail?: string }
let activeSession: CameraIntercomSession | null = null;

/** One deliberate microphone session per tab, isolated from all live decoders. */
export class CameraIntercomSession {
  private frame: HTMLIFrameElement | null = null;
  private stopped = false;
  private abort = new AbortController();
  private deadline?: ReturnType<typeof setTimeout>;
  private stopPromise?: Promise<void>;
  private released?: () => void;
  private id = `intercom-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  constructor(private cameraId: number, private notify: (state: IntercomState) => void) {}

  private receive = (event: MessageEvent) => {
    if (event.source !== this.frame?.contentWindow || event.origin !== window.location.origin || event.data?.id !== this.id) return;
    const { type, message } = event.data;
    if (type === 'stopped') { this.released?.(); return; }
    if (this.stopped) return;
    if (type === 'progress' && typeof message === 'string') {
      this.notify({ phase:'connecting', detail:message.slice(0,200) });
    } else if (type === 'diagnostic') {
      console.warn('Camera intercom connection diagnostic', { cameraId:this.cameraId, ...message });
    } else if (type === 'active') {
      clearTimeout(this.deadline);
      this.notify({ phase:'active' });
    } else if (type === 'error') {
      void this.stop().then(() => this.notify({ phase:'error', error: typeof message === 'string' ? message : 'Intercom connection failed.' }));
    }
  };

  private hide = () => { if (document.hidden) void this.stop(); };
  private leave = () => { void this.stop(); };

  async start() {
    const previous = activeSession;
    activeSession = this;
    this.notify({ phase:'connecting' });
    if (previous && previous !== this) await previous.stop();
    if (this.stopped) return;
    this.deadline = setTimeout(() => {
      void this.stop().then(() => this.notify({ phase:'error', error:'Intercom connection timed out. Retry the microphone.' }));
    }, 45000);
    try {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        throw new Error('Microphone access requires HTTPS or localhost and a browser that supports audio calling.');
      }
      const connection = await apiClient.post<object>(`/camera-security/camera/${this.cameraId}/intercom`, undefined, { signal:this.abort.signal });
      if (this.stopped) return;
      const frame = document.createElement('iframe');
      this.frame = frame;
      frame.title = 'Camera intercom audio connection';
      frame.src = '/ezopen/intercom.html?v=4';
      frame.allow = 'microphone; autoplay';
      // Keep the SDK globals, audio elements and WebSocket adapter out of Live View.
      frame.style.cssText = 'position:fixed;width:1px;height:1px;left:-100px;top:-100px;border:0;';
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      window.addEventListener('message', this.receive);
      window.addEventListener('pagehide', this.leave);
      document.addEventListener('visibilitychange', this.hide);
      frame.onload = () => {
        if (!this.stopped) frame.contentWindow?.postMessage({ type:'start', id:this.id, connection }, window.location.origin);
      };
      document.body.appendChild(frame);
    } catch (error: any) {
      if (this.stopped) return;
      const message = error.response?.status === 404
        ? 'The connected API does not expose intercom. Restart or update that API, refresh this page and retry.'
        : error.response?.data?.detail || error.message || 'Unable to connect the camera microphone.';
      await this.stop();
      this.notify({ phase:'error', error: typeof message === 'string' ? message : 'Unable to connect the camera microphone.' });
    }
  }

  stop(): Promise<void> {
    if (this.stopPromise) return this.stopPromise;
    this.stopped = true;
    this.abort.abort();
    clearTimeout(this.deadline);
    this.notify({ phase:'stopping' });
    this.stopPromise = (async () => {
      if (this.frame) {
        await new Promise<void>(resolve => {
          const timer = setTimeout(resolve, 6000);
          this.released = () => { clearTimeout(timer); resolve(); };
          this.frame?.contentWindow?.postMessage({ type:'stop', id:this.id }, window.location.origin);
        });
        this.frame.remove();
        this.frame = null;
      }
      window.removeEventListener('message', this.receive);
      window.removeEventListener('pagehide', this.leave);
      document.removeEventListener('visibilitychange', this.hide);
      if (activeSession === this) activeSession = null;
      this.notify({ phase:'idle' });
    })();
    return this.stopPromise;
  }
}
